import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  FileCheck, ShieldCheck, XCircle, Clock, Camera, CheckCircle2,
  AlertTriangle, Car, User, Wrench, CalendarCheck, PackageCheck,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatPrice, PART_NAMES, SEVERITY_NAMES, CAUSE_NAMES } from './damage-valuation';

const CLAIM_STATUS = {
  submitted: { label: 'Enviado', class: 'bg-blue-100 text-blue-700 border-blue-200' },
  under_review: { label: 'En revisión', class: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  approved: { label: 'Aprobado', class: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  rejected: { label: 'Rechazado', class: 'bg-rose-100 text-rose-700 border-rose-200' },
  requires_revision: { label: 'Requiere revisión', class: 'bg-amber-100 text-amber-700 border-amber-200' },
};

const PHOTO_TYPE_LABELS = { antes: 'Antes', durante: 'Durante', despues: 'Después' };

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('es-CR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const ClaimsConsole = ({ onChanged }) => {
  const { API, sessionToken } = useContext(AuthContext);
  const headers = { Authorization: `Bearer ${sessionToken}` };

  const [claims, setClaims] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Decision form
  const [approvalType, setApprovalType] = useState('total');
  const [approvedAmount, setApprovedAmount] = useState('');
  const [deductible, setDeductible] = useState('');
  const [expectedDelivery, setExpectedDelivery] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [reason, setReason] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [claimsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/damage-valuations`, { headers }),
        axios.get(`${API}/admin/claims/stats`, { headers }),
      ]);
      setClaims(claimsRes.data.filter((c) => c.status !== 'draft'));
      setStats(statsRes.data);
    } catch {
      toast.error('Error al cargar los siniestros');
    } finally {
      setLoading(false);
    }
  }, [API, sessionToken]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openDetail = async (claimId) => {
    try {
      const res = await axios.get(`${API}/admin/damage-valuations/${claimId}/detail`, { headers });
      setDetail(res.data);
      const v = res.data.valuation;
      setApprovalType('total');
      setApprovedAmount('');
      setDeductible(v.deductible ? String(v.deductible) : (res.data.vehicle?.insurance_info?.deductible ? String(res.data.vehicle.insurance_info.deductible) : ''));
      setExpectedDelivery('');
      setAdminNotes('');
      setReason('');
      setDetailOpen(true);
    } catch {
      toast.error('No se pudo cargar el detalle del siniestro');
    }
  };

  const refreshAfterAction = async () => {
    await fetchData();
    if (onChanged) onChanged();
  };

  const decide = async (action) => {
    if (!detail) return;
    const payload = { action };
    if (action === 'approved') {
      payload.approval_type = approvalType;
      if (approvalType === 'parcial') {
        const amt = parseFloat(approvedAmount || 0);
        if (!amt || amt <= 0) { toast.error('Indica el monto aprobado parcial'); return; }
        payload.approved_amount = amt;
      }
      if (deductible !== '') payload.deductible = parseFloat(deductible || 0);
      if (expectedDelivery) payload.expected_delivery_date = expectedDelivery;
    } else {
      if (!reason.trim()) { toast.error('Indica el motivo'); return; }
      payload.reason = reason;
    }
    if (adminNotes.trim()) payload.admin_notes = adminNotes;

    try {
      setSubmitting(true);
      await axios.post(`${API}/admin/damage-valuations/${detail.valuation.id}/review`, payload, { headers });
      toast.success(action === 'approved' ? 'Siniestro aprobado' : action === 'rejected' ? 'Siniestro rechazado' : 'Revisión solicitada al taller');
      setDetailOpen(false);
      await refreshAfterAction();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al procesar la decisión');
    } finally {
      setSubmitting(false);
    }
  };

  const markDelivered = async () => {
    if (!detail) return;
    try {
      setSubmitting(true);
      await axios.post(`${API}/admin/damage-valuations/${detail.valuation.id}/delivered`, {}, { headers });
      toast.success('Entrega registrada');
      setDetailOpen(false);
      await refreshAfterAction();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al registrar la entrega');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredClaims = claims.filter((c) => statusFilter === 'all' || c.status === statusFilter);
  const v = detail?.valuation;
  const canDecide = v && ['submitted', 'under_review'].includes(v.status);

  if (loading) {
    return <Card className="p-12 text-center text-slate-500" data-testid="claims-loading">Cargando siniestros…</Card>;
  }

  return (
    <div className="space-y-4" data-testid="claims-console">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <ClaimStat testid="claims-stat-pending" icon={<Clock className="w-4 h-4 text-blue-600" />} label="Pendientes" value={stats?.pending_claims ?? 0} bg="bg-blue-50" />
        <ClaimStat testid="claims-stat-approved" icon={<CheckCircle2 className="w-4 h-4 text-emerald-600" />} label="Aprobados" value={stats?.approved_claims ?? 0} bg="bg-emerald-50" />
        <ClaimStat testid="claims-stat-rejected" icon={<XCircle className="w-4 h-4 text-rose-600" />} label="Rechazados" value={stats?.rejected_claims ?? 0} bg="bg-rose-50" />
        <ClaimStat testid="claims-stat-amount" icon={<ShieldCheck className="w-4 h-4 text-emerald-600" />} label="Monto aprobado" value={formatPrice(stats?.total_approved_amount ?? 0)} bg="bg-emerald-50" small />
        <ClaimStat testid="claims-stat-deductibles" icon={<FileCheck className="w-4 h-4 text-purple-600" />} label="Deducibles" value={formatPrice(stats?.total_deductibles ?? 0)} bg="bg-purple-50" small />
      </div>

      {/* Filter */}
      <Card className="p-4 flex items-center justify-between">
        <h3 className="font-semibold text-slate-900">Siniestros ({filteredClaims.length})</h3>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-52" data-testid="claims-status-filter">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="submitted">Enviados</SelectItem>
            <SelectItem value="approved">Aprobados</SelectItem>
            <SelectItem value="rejected">Rechazados</SelectItem>
            <SelectItem value="requires_revision">Requieren revisión</SelectItem>
          </SelectContent>
        </Select>
      </Card>

      {/* Claims table */}
      <Card className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-100 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3">Reclamo</th>
              <th className="px-4 py-3">Taller</th>
              <th className="px-4 py-3">Cliente / Vehículo</th>
              <th className="px-4 py-3">Estimado</th>
              <th className="px-4 py-3">Aprobado</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filteredClaims.map((c) => {
              const badge = CLAIM_STATUS[c.status] || { label: c.status, class: 'bg-slate-100 text-slate-600' };
              return (
                <tr key={c.id} data-testid={`claim-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{c.claim_number || c.id.slice(0, 8)}</div>
                    <div className="text-xs text-slate-500">{c.insurance_company} · {fmtDate(c.submitted_at || c.created_at)}</div>
                  </td>
                  <td className="px-4 py-3 text-sm">{c.taller_info?.company_name || c.taller_info?.name || '—'}</td>
                  <td className="px-4 py-3 text-sm">
                    <div>{c.client_info?.name || '—'}</div>
                    <div className="text-xs text-slate-500">
                      {c.vehicle_snapshot?.make} {c.vehicle_snapshot?.model} · {c.vehicle_snapshot?.license_plate}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium">{formatPrice(c.total_estimate || 0)}</td>
                  <td className="px-4 py-3 text-sm">
                    {c.status === 'approved' ? (
                      <div>
                        <span className="font-medium text-emerald-700">{formatPrice(c.approved_amount || 0)}</span>
                        <div className="text-xs text-slate-500">{c.approval_type === 'parcial' ? 'Parcial' : 'Total'}{c.deductible > 0 ? ` · Ded. ${formatPrice(c.deductible)}` : ''}</div>
                      </div>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded border ${badge.class}`}>{badge.label}</span>
                    {c.status === 'approved' && c.delivered_at && (
                      <div className="text-xs text-emerald-600 mt-1 flex items-center gap-1"><PackageCheck className="w-3 h-3" />Entregado</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => openDetail(c.id)} data-testid={`view-claim-${c.id}`}>
                      Ver detalle {c.photos_count > 0 && <span className="ml-1 text-xs text-slate-500">({c.photos_count} <Camera className="w-3 h-3 inline" />)</span>}
                    </Button>
                  </td>
                </tr>
              );
            })}
            {filteredClaims.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No hay siniestros con este filtro.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {/* Detail dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="claim-detail-dialog">
          {v && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  <ShieldCheck className="w-6 h-6 text-purple-600" />
                  Siniestro {v.claim_number || v.id.slice(0, 8)}
                  <span className={`text-xs px-2 py-1 rounded border ${(CLAIM_STATUS[v.status] || {}).class || ''}`}>
                    {(CLAIM_STATUS[v.status] || { label: v.status }).label}
                  </span>
                  {v.status === 'approved' && (
                    <Badge className={v.approval_type === 'parcial' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}>
                      Aprobación {v.approval_type === 'parcial' ? 'parcial' : 'total'}
                    </Badge>
                  )}
                </DialogTitle>
              </DialogHeader>

              {/* Parties & vehicle */}
              <div className="grid sm:grid-cols-3 gap-3">
                <InfoBox icon={<User className="w-4 h-4 text-blue-600" />} title="Cliente">
                  <p className="font-medium">{detail.client_info?.name || '—'}</p>
                  <p className="text-xs text-slate-500">{detail.client_info?.phone || detail.client_info?.email || ''}</p>
                </InfoBox>
                <InfoBox icon={<Wrench className="w-4 h-4 text-purple-600" />} title="Taller">
                  <p className="font-medium">{detail.taller_info?.company_name || detail.taller_info?.name || '—'}</p>
                  <p className="text-xs text-slate-500">{detail.taller_info?.phone || detail.taller_info?.email || ''}</p>
                </InfoBox>
                <InfoBox icon={<Car className="w-4 h-4 text-indigo-600" />} title="Vehículo">
                  <p className="font-medium">{v.vehicle_snapshot?.make} {v.vehicle_snapshot?.model} {v.vehicle_snapshot?.year}</p>
                  <p className="text-xs text-slate-500">Placa: {v.vehicle_snapshot?.license_plate || '—'}</p>
                </InfoBox>
              </div>

              {/* Policy info */}
              <Card className="p-4 bg-purple-50 border-purple-200" data-testid="claim-policy-info">
                <h4 className="text-sm font-semibold text-purple-900 mb-2 flex items-center gap-2"><FileCheck className="w-4 h-4" />Póliza del asegurado</h4>
                <div className="grid sm:grid-cols-4 gap-3 text-sm">
                  <div><span className="text-slate-500 block text-xs">Aseguradora</span>{v.insurance_company || '—'}</div>
                  <div><span className="text-slate-500 block text-xs">N° Póliza</span>{v.policy_number || detail.vehicle?.insurance_info?.policy_number || '—'}</div>
                  <div><span className="text-slate-500 block text-xs">Deducible</span>{(v.deductible || detail.vehicle?.insurance_info?.deductible) ? formatPrice(v.deductible || detail.vehicle?.insurance_info?.deductible) : '—'}</div>
                  <div><span className="text-slate-500 block text-xs">Código agencia</span>{v.agency_code || '—'}</div>
                </div>
              </Card>

              {/* Incident */}
              <Card className="p-4">
                <h4 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" />Incidente</h4>
                <div className="grid sm:grid-cols-3 gap-3 text-sm mb-2">
                  <div><span className="text-slate-500 block text-xs">Causa</span>{CAUSE_NAMES[v.incident_cause] || v.incident_cause}</div>
                  <div><span className="text-slate-500 block text-xs">Fecha</span>{fmtDate(v.incident_date)}</div>
                  <div><span className="text-slate-500 block text-xs">Ubicación</span>{v.incident_location || '—'}</div>
                </div>
                <p className="text-sm text-slate-700">{v.incident_description}</p>
              </Card>

              {/* Damaged parts + costs */}
              <Card className="p-4">
                <h4 className="text-sm font-semibold text-slate-900 mb-2">Partes dañadas ({(v.damaged_parts || []).length})</h4>
                <div className="divide-y">
                  {(v.damaged_parts || []).map((p, i) => (
                    <div key={i} className="py-2 flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span>{PART_NAMES[p.part_name] || p.part_name}</span>
                        <Badge className={`text-xs ${(SEVERITY_NAMES[p.severity] || {}).color || 'bg-slate-100'}`}>
                          {(SEVERITY_NAMES[p.severity] || { label: p.severity }).label}
                        </Badge>
                        {p.requires_replacement && <span className="text-xs text-rose-600">Reemplazo</span>}
                      </div>
                      <span className="font-medium">{formatPrice(p.estimated_cost || 0)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t space-y-1 text-sm">
                  <CostRow label="Repuestos" value={v.parts_cost} />
                  <CostRow label="Mano de obra" value={v.labor_cost} />
                  <CostRow label="Pintura" value={v.paint_cost} />
                  <CostRow label="Otros" value={v.other_costs} />
                  <CostRow label={`IVA (${v.tax_rate}%)`} value={v.tax_amount} />
                  <div className="flex justify-between pt-2 border-t font-bold text-base">
                    <span>Total estimado</span>
                    <span data-testid="claim-total-estimate">{formatPrice(v.total_estimate || 0)}</span>
                  </div>
                  {v.status === 'approved' && (
                    <>
                      <div className="flex justify-between text-emerald-700 font-semibold">
                        <span>Monto aprobado ({v.approval_type})</span>
                        <span data-testid="claim-approved-amount">{formatPrice(v.approved_amount || 0)}</span>
                      </div>
                      {v.deductible > 0 && (
                        <div className="flex justify-between text-purple-700">
                          <span>Deducible a cargo del cliente</span>
                          <span>{formatPrice(v.deductible)}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </Card>

              {/* Dates timeline */}
              <Card className="p-4" data-testid="claim-dates">
                <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2"><CalendarCheck className="w-4 h-4 text-indigo-600" />Fechas del siniestro</h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div><span className="text-slate-500 block text-xs">Enviado</span>{fmtDate(v.submitted_at)}</div>
                  <div><span className="text-slate-500 block text-xs">Aprobado</span>{fmtDate(v.approved_at)}</div>
                  <div><span className="text-slate-500 block text-xs">Entrega estimada</span>{fmtDate(v.expected_delivery_date)}</div>
                  <div>
                    <span className="text-slate-500 block text-xs">Entregado</span>
                    {v.delivered_at ? fmtDate(v.delivered_at) : (
                      v.status === 'approved' ? (
                        <Button size="sm" variant="outline" className="mt-1 h-7 text-xs" onClick={markDelivered} disabled={submitting} data-testid="mark-delivered-btn">
                          <PackageCheck className="w-3 h-3 mr-1" />Marcar entregado
                        </Button>
                      ) : '—'
                    )}
                  </div>
                </div>
                {(v.rejection_reason || v.admin_notes) && (
                  <div className="mt-3 pt-3 border-t text-sm space-y-1">
                    {v.rejection_reason && <p className="text-rose-700"><strong>Motivo:</strong> {v.rejection_reason}</p>}
                    {v.admin_notes && <p className="text-slate-600"><strong>Notas del analista:</strong> {v.admin_notes}</p>}
                  </div>
                )}
              </Card>

              {/* Repair workflow */}
              <Card className="p-4" data-testid="claim-workflow-section">
                <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Wrench className="w-4 h-4 text-purple-600" />Workflow de reparación
                </h4>
                {detail.repair_progress?.stages?.length ? (
                  <div className="space-y-0">
                    {detail.repair_progress.stages.map((s, i) => (
                      <div key={s.stage} className="flex gap-3" data-testid={`workflow-stage-${s.stage}`}>
                        <div className="flex flex-col items-center">
                          {s.status === 'completed' ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                          ) : s.status === 'in_progress' ? (
                            <Clock className="w-5 h-5 text-blue-500 shrink-0" />
                          ) : (
                            <div className="w-5 h-5 rounded-full border-2 border-slate-200 shrink-0" />
                          )}
                          {i < detail.repair_progress.stages.length - 1 && (
                            <div className={`w-0.5 flex-1 min-h-[16px] ${s.status === 'completed' ? 'bg-emerald-300' : 'bg-slate-200'}`} />
                          )}
                        </div>
                        <div className="pb-3">
                          <p className={`text-sm ${s.status === 'pending' ? 'text-slate-400' : 'font-medium text-slate-900'}`}>
                            {s.label}
                            {s.status === 'in_progress' && <span className="ml-2 text-xs text-blue-600 font-normal">En curso</span>}
                          </p>
                          {s.updated_at && <p className="text-xs text-slate-400">{fmtDate(s.updated_at)}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">La reparación aún no ha iniciado (sin cita registrada con el taller).</p>
                )}
              </Card>

              {/* Photos */}
              <Card className="p-4" data-testid="claim-photos-section">
                <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Camera className="w-4 h-4 text-slate-600" />Fotos de la reparación ({detail.photos.length})
                </h4>
                {detail.photos.length === 0 ? (
                  <p className="text-sm text-slate-400">El taller aún no ha subido fotos.</p>
                ) : (
                  ['antes', 'durante', 'despues'].map((type) => {
                    const group = detail.photos.filter((p) => p.photo_type === type);
                    if (group.length === 0) return null;
                    return (
                      <div key={type} className="mb-3">
                        <p className="text-xs font-medium text-slate-500 uppercase mb-2">{PHOTO_TYPE_LABELS[type]}</p>
                        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                          {group.map((p) => (
                            <a key={p.id} href={`${API}/photos/${p.id}`} target="_blank" rel="noreferrer">
                              <img
                                src={`${API}/photos/${p.id}`}
                                alt={`Foto ${PHOTO_TYPE_LABELS[type]}`}
                                className="w-full h-20 object-cover rounded-lg border hover:opacity-80 transition-opacity"
                                loading="lazy"
                              />
                            </a>
                          ))}
                        </div>
                      </div>
                    );
                  })
                )}
              </Card>

              {/* Decision panel */}
              {canDecide && (
                <Card className="p-4 border-purple-200 bg-purple-50/50" data-testid="claim-decision-panel">
                  <h4 className="text-sm font-semibold text-slate-900 mb-3">Resolución del siniestro</h4>
                  <div className="grid sm:grid-cols-2 gap-3 mb-3">
                    <div>
                      <Label className="text-xs">Tipo de aprobación</Label>
                      <Select value={approvalType} onValueChange={setApprovalType}>
                        <SelectTrigger data-testid="approval-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="total">Total ({formatPrice(v.total_estimate || 0)})</SelectItem>
                          <SelectItem value="parcial">Parcial</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {approvalType === 'parcial' && (
                      <div>
                        <Label className="text-xs">Monto aprobado (₡)</Label>
                        <Input type="number" min="0" value={approvedAmount} onChange={(e) => setApprovedAmount(e.target.value)} placeholder={`Máx. ${Math.round(v.total_estimate || 0)}`} data-testid="approved-amount-input" />
                      </div>
                    )}
                    <div>
                      <Label className="text-xs">Deducible (₡)</Label>
                      <Input type="number" min="0" value={deductible} onChange={(e) => setDeductible(e.target.value)} placeholder="Ej: 150000" data-testid="deductible-input" />
                    </div>
                    <div>
                      <Label className="text-xs">Fecha estimada de entrega</Label>
                      <Input type="date" value={expectedDelivery} onChange={(e) => setExpectedDelivery(e.target.value)} data-testid="expected-delivery-input" />
                    </div>
                  </div>
                  <div className="space-y-2 mb-3">
                    <Textarea value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} placeholder="Notas del analista (opcional)" rows={2} data-testid="admin-notes-input" />
                    <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo (requerido para rechazar o solicitar revisión)" rows={2} data-testid="reject-reason-input" />
                  </div>
                  <div className="flex flex-wrap gap-2 justify-end">
                    <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => decide('requires_revision')} disabled={submitting} data-testid="revision-claim-btn">
                      <AlertTriangle className="w-4 h-4 mr-1" />Solicitar revisión
                    </Button>
                    <Button variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => decide('rejected')} disabled={submitting} data-testid="reject-claim-btn">
                      <XCircle className="w-4 h-4 mr-1" />Rechazar
                    </Button>
                    <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => decide('approved')} disabled={submitting} data-testid="approve-claim-btn">
                      <CheckCircle2 className="w-4 h-4 mr-1" />Aprobar {approvalType === 'parcial' ? 'parcial' : 'total'}
                    </Button>
                  </div>
                </Card>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ClaimStat = ({ icon, label, value, bg, small, testid }) => (
  <Card className={`p-4 ${bg}`} data-testid={testid}>
    <div className="flex items-center gap-2 mb-1">{icon}<span className="text-xs text-slate-600">{label}</span></div>
    <p className={`font-bold text-slate-900 ${small ? 'text-lg' : 'text-2xl'}`}>{value}</p>
  </Card>
);

const InfoBox = ({ icon, title, children }) => (
  <Card className="p-3">
    <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">{icon}{title}</div>
    <div className="text-sm">{children}</div>
  </Card>
);

const CostRow = ({ label, value }) => (
  <div className="flex justify-between text-slate-600">
    <span>{label}</span>
    <span>{new Intl.NumberFormat('es-CR', { style: 'currency', currency: 'CRC', maximumFractionDigits: 0 }).format(value || 0)}</span>
  </div>
);

export default ClaimsConsole;
